import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { FontPreview } from '../components/FontPreview';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, Loader2, Download, ArrowRight, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, isAuthenticated, loading: authLoading, refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error, pending
  const [fontData, setFontData] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const maxPolls = 10;

  const sessionId = searchParams.get('session_id');
  const purchaseType = searchParams.get('type'); // 'preview' for preview downloads

  // Get token directly from localStorage as fallback
  const storedToken = localStorage.getItem('token');
  const effectiveToken = token || storedToken;

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) {
      return;
    }

    // Mark that we've done the auth check
    setAuthChecked(true);

    if (!sessionId) {
      setStatus('error');
      return;
    }

    // Check if user has a token (from context or localStorage)
    // Only redirect to login if there's no token at all
    if (!effectiveToken) {
      console.log('No token found, redirecting to login');
      navigate('/login');
      return;
    }

    pollPaymentStatus();
  }, [sessionId, effectiveToken, authLoading]);

  const pollPaymentStatus = async () => {
    if (pollCount >= maxPolls) {
      setStatus('pending');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/checkout/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );

      const { payment_status, font_id, font_ids, status: sessionStatus, type, format } = response.data;
      setTransactionData(response.data);

      if (payment_status === 'paid') {
        setStatus('success');
        
        // Refresh user data to get updated credits
        if (refreshUser) {
          await refreshUser();
        }
        
        // Fetch font details
        if (font_id) {
          const fontResponse = await axios.get(`${API_URL}/api/fonts/${font_id}`);
          setFontData(fontResponse.data);
        } else if (font_ids && font_ids.length > 0) {
          // For bundles, just get the first font for display
          const fontResponse = await axios.get(`${API_URL}/api/fonts/${font_ids[0]}`);
          setFontData(fontResponse.data);
        }
        toast.success('Payment successful!');
      } else if (sessionStatus === 'expired') {
        setStatus('error');
      } else {
        // Continue polling
        setPollCount((prev) => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Status check error:', error);
      
      // If unauthorized (401), redirect to login
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
        return;
      }
      
      setPollCount((prev) => prev + 1);
      if (pollCount < maxPolls - 1) {
        setTimeout(pollPaymentStatus, 2000);
      } else {
        setStatus('error');
      }
    }
  };

  const handleDownload = () => {
    if (!fontData) return;

    const cssContent = `/* ${fontData.name} - Generated by FuntzGuntzAI */
/* Prompt: ${fontData.prompt} */

@font-face {
  font-family: '${fontData.name}';
  src: local('${fontData.css_properties?.fontFamily || 'Inter'}');
  font-weight: ${fontData.style_properties?.weight || 400};
  font-style: ${fontData.style_properties?.style || 'normal'};
}

.${fontData.name.toLowerCase().replace(/\s+/g, '-')} {
  font-family: '${fontData.name}', ${fontData.css_properties?.fontFamily || 'sans-serif'};
  font-weight: ${fontData.style_properties?.weight || 400};
  font-style: ${fontData.style_properties?.style || 'normal'};
  letter-spacing: ${fontData.style_properties?.letterSpacing || 'normal'};
  line-height: ${fontData.style_properties?.lineHeight || '1.5'};
  text-transform: ${fontData.style_properties?.textTransform || 'none'};
}
`;

    const blob = new Blob([cssContent], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fontData.name.toLowerCase().replace(/\s+/g, '-')}.css`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Font CSS downloaded!');
  };

  const handlePreviewDownload = async () => {
    if (!fontData || !transactionData) return;
    
    setDownloading(true);
    try {
      const format = transactionData.format || 'jpg';
      // Fixed endpoint: use path params instead of query params
      const response = await axios.get(
        `${API_URL}/api/preview-download/${fontData.id}/${format}`,
        { 
          headers: { Authorization: `Bearer ${effectiveToken}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fontData.name}_5000x5000.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Preview ${format.toUpperCase()} downloaded!`);
    } catch (error) {
      console.error('Download failed:', error);
      // Show more specific error message
      const errorMessage = error.response?.data?.detail || 
                          error.response?.statusText || 
                          error.message || 
                          'Failed to download preview';
      toast.error(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="checkout-success-page">
      <Header />
      
      <main className="pt-32 pb-16 px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          {status === 'loading' && (
            <div className="space-y-6">
              <div className="w-20 h-20 mx-auto flex items-center justify-center border border-[#FF3300]/30 animate-pulse-glow">
                <Loader2 className="w-10 h-10 text-[#FF3300] animate-spin" />
              </div>
              <h1 
                className="text-4xl tracking-tight text-[#0A0A0A]"
                style={{ fontFamily: 'Bodoni Moda, serif' }}
              >
                Processing Payment...
              </h1>
              <p className="text-[#525252]">
                Please wait while we confirm your payment
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-8">
              <div className="w-20 h-20 mx-auto flex items-center justify-center bg-green-500/10 border border-green-500/30">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              
              <div>
                <h1 
                  className="text-5xl tracking-tight mb-4 text-[#0A0A0A]"
                  style={{ fontFamily: 'Bodoni Moda, serif' }}
                >
                  Payment <span className="text-[#FF3300]">Successful</span>
                </h1>
                <p className="text-[#525252] text-lg">
                  {transactionData?.type === 'credits_purchase' 
                    ? '50 Credits for Font Generation have been added to your account!'
                    : transactionData?.type === 'preview_download' 
                    ? 'Your high-resolution preview is ready to download'
                    : transactionData?.type === 'bundle'
                    ? 'Your font bundle has been added to your collection'
                    : 'Your font has been added to your collection'}
                </p>
              </div>

              {/* Credits purchase success - show credits info */}
              {transactionData?.type === 'credits_purchase' && (
                <div className="card-surface p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-white border border-[#FF3300]/30 rounded-full">
                    <span className="text-3xl font-bold text-[#FF3300]">50</span>
                  </div>
                  <h3 className="text-xl font-semibold text-[#0A0A0A] mb-2">Generation Credits Added</h3>
                  <p className="text-[#525252] mb-6">
                    You can now generate up to 50 unique AI fonts. Each generation uses 1 credit.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      onClick={() => navigate('/dashboard')}
                      variant="outline"
                      className="flex-1 border-[#E5E5E5] hover:bg-[#0A0A0A] hover:text-white py-6 rounded-none uppercase tracking-wider"
                      data-testid="go-dashboard-credits-btn"
                    >
                      View Dashboard
                    </Button>
                    <Button
                      onClick={() => navigate('/generator')}
                      className="flex-1 bg-[#FF3300] hover:bg-[#E62E00] py-6 rounded-none uppercase tracking-wider"
                      data-testid="start-generating-btn"
                    >
                      Start Generating
                    </Button>
                  </div>
                </div>
              )}

              {fontData && transactionData?.type !== 'credits_purchase' && (
                <div className="card-surface p-8 text-left">
                  <span className="mono-label">{fontData.name}</span>
                  
                  {/* Preview with border style for preview_download type */}
                  {transactionData?.type === 'preview_download' ? (
                    <div className="my-6">
                      <FontPreview 
                        font={{
                          id: fontData.id,
                          name: fontData.name,
                          preview_text: transactionData.preview_text || fontData.preview_text,
                          css_properties: fontData.css_properties,
                          style_properties: fontData.style_properties
                        }}
                        borderStyle={transactionData.border_style || 'none'}
                        className="max-w-sm mx-auto"
                      />
                    </div>
                  ) : (
                    <div className="my-6 min-h-[80px] flex items-center justify-center">
                      <p 
                        className="text-3xl text-[#0A0A0A]"
                        style={{
                          fontFamily: fontData.css_properties?.fontFamily || 'Inter',
                          fontWeight: fontData.style_properties?.weight || 400,
                          fontStyle: fontData.style_properties?.style || 'normal',
                        }}
                      >
                        {fontData.preview_text}
                      </p>
                    </div>
                  )}
                  
                  <p className="text-[#525252] text-sm mb-6">{fontData.description}</p>
                  
                  {/* Conditional download buttons based on purchase type */}
                  {transactionData?.type === 'preview_download' ? (
                    <Button
                      onClick={handlePreviewDownload}
                      disabled={downloading}
                      className="w-full bg-[#FF3300] hover:bg-[#E62E00] py-6 rounded-none uppercase tracking-wider"
                      data-testid="download-preview-btn"
                    >
                      {downloading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Download {(transactionData.format || 'jpg').toUpperCase()} (5000×5000px)
                        </>
                      )}
                    </Button>
                  ) : transactionData?.type === 'bundle' ? (
                    <Button
                      onClick={handleDownload}
                      className="w-full bg-[#FF3300] hover:bg-[#E62E00] py-6 rounded-none uppercase tracking-wider"
                      data-testid="download-bundle-btn"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Bundle CSS
                    </Button>
                  ) : (
                    <Button
                      onClick={handleDownload}
                      className="w-full bg-[#FF3300] hover:bg-[#E62E00] py-6 rounded-none uppercase tracking-wider"
                      data-testid="download-btn"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Font CSS
                    </Button>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="border-[#E5E5E5] hover:bg-[#0A0A0A] hover:text-white rounded-none px-8"
                  data-testid="go-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/generator')}
                  className="bg-[#FF3300] hover:bg-[#E62E00] rounded-none px-8"
                  data-testid="create-another-btn"
                >
                  Create Another Font
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {status === 'pending' && (
            <div className="space-y-6">
              <div className="w-20 h-20 mx-auto flex items-center justify-center border border-yellow-500/30 bg-yellow-500/10">
                <AlertCircle className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 
                className="text-4xl tracking-tight text-[#0A0A0A]"
                style={{ fontFamily: 'Bodoni Moda, serif' }}
              >
                Payment Pending
              </h1>
              <p className="text-[#525252]">
                Your payment is still being processed. Please check your dashboard in a few minutes.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#FF3300] hover:bg-[#E62E00] rounded-none px-8"
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="w-20 h-20 mx-auto flex items-center justify-center border border-red-500/30 bg-white">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 
                className="text-4xl tracking-tight text-[#0A0A0A]"
                style={{ fontFamily: 'Bodoni Moda, serif' }}
              >
                Something Went Wrong
              </h1>
              <p className="text-[#525252]">
                We couldn't verify your payment. Please contact support if you were charged.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/generator')}
                  variant="outline"
                  className="border-[#E5E5E5] hover:bg-[#0A0A0A] hover:text-white rounded-none px-8"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#FF3300] hover:bg-[#E62E00] rounded-none px-8"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
